import React, { useEffect, useRef, useState } from 'react';
import { DownloadIcon, Loader2Icon, PrinterIcon, XIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { captureToPdf } from '../../utils/pdfCapture';
type Props = {
  title: string;
  filename: string;
  paper?: 'A4' | 'Letter';
  onClose: () => void;
  children: React.ReactNode;
  initialAction?: 'view' | 'print' | 'download';
};

/**
 * Print / PDF preview. Printing isolates this dialog with the shared
 * printing-test-modal / test-modal-print styles; PDF uses html2canvas + jsPDF.
 */
export function BillingPrintModal({
  title,
  filename,
  paper = 'A4',
  onClose,
  children,
  initialAction = 'view'
}: Props) {
  const paperRef = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const print = () => {
    document.body.classList.add('printing-test-modal');
    const cleanup = () => {
      document.body.classList.remove('printing-test-modal');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
    window.setTimeout(cleanup, 1500);
  };
  const download = async () => {
    if (!paperRef.current) return;
    setBusy(true);
    setError('');
    try {
      await captureToPdf(paperRef.current, filename, paper);
    } catch (e) {
      console.error('PDF generation failed', e);
      setError('The PDF could not be created. Check your connection (the logo must load) and try again.');
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => {
      if (initialAction === 'print') print();
      if (initialAction === 'download') void download();
    }, 400);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <div className="test-modal-print fixed inset-0 z-[60] flex flex-col bg-slate-900/70 print:static print:bg-white" role="dialog" aria-modal="true" aria-label={title} data-id="element-3027" data-mp="k2c4u33phe6e">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-700 bg-slate-900 px-4 py-2.5 text-white print:hidden" data-id="element-3028" data-mp="b2yw5cneotmq">
        <h2 className="mr-auto truncate text-sm font-semibold" data-id="element-3029" data-mp="f528e3hwg292">{title}</h2>
        {error && <p role="alert" className="w-full rounded-md bg-red-500/15 px-3 py-1.5 text-xs text-red-100 sm:order-none sm:w-auto" data-id="element-3030" data-mp="12tlhgl8rwj27">{error}</p>}
        <Button size="sm" variant="secondary" onClick={print} data-id="element-3031" data-mp="17s6gihcagwp2"><PrinterIcon className="mr-1.5 h-4 w-4" aria-hidden="true" data-id="element-3032" data-mp="u22yy3octr5y" />Print</Button>
        <Button size="sm" onClick={download} disabled={busy} data-id="element-3033" data-mp="y2wp73p9ri20">
          {busy ? <Loader2Icon className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" data-id="element-3034" data-mp="y121xiqizdur" /> : <DownloadIcon className="mr-1.5 h-4 w-4" aria-hidden="true" data-id="element-3035" data-mp="1lr673ujd3lnv" />}
          {busy ? 'Creating PDF…' : 'Download PDF'}
        </Button>
        <button type="button" onClick={onClose} className="rounded-md p-1.5 text-slate-300 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" aria-label="Close preview" data-id="element-3036" data-mp="3b9uzh8i72rp6">
          <XIcon className="h-5 w-5" data-id="element-3037" data-mp="3kzo75yf7umo5" />
        </button>
      </div>
      <div className="flex-1 overflow-auto p-4 sm:p-8 print:overflow-visible print:p-0" data-id="element-3038" data-mp="3kywccptg0syx">
        <div className="mx-auto w-fit shadow-xl print:shadow-none" data-id="element-3039" data-mp="3g13842bpa91a">
          <div ref={paperRef} data-id="element-3040" data-mp="2sa7b8qse0nn6">{children}</div>
        </div>
      </div>
    </div>;
}